/**
 * Master-solution EXTRACTION (reading an uploaded solution) — one job per exam.
 *
 * Per exam rather than per main task for the same reason as `structure.job.ts`: the step opens with one
 * `runBoundingBoxAgent` pass over the uploaded files and the entire task tree, and only then fans out over
 * the regions it found. A per-task split would re-send every page once per task.
 *
 * No `desired`. This is the one that looks derivable and is not: after a successful run the exam reads
 * "master solution uploaded, extraction COMPLETED", and that is EXACTLY what it reads after a RE-upload too,
 * right up until the step itself resets the progress. So state cannot distinguish "already done" from "done,
 * then re-uploaded", and a `desired` would either miss re-uploads or re-extract forever. The upload site
 * enqueues it.
 *
 * RUNS ALONGSIDE GRADING-SCHEME EXTRACTION. It used to persist the whole rebuilt `tasks` array, which meant
 * whichever of the two finished second overwrote the other's artifact with its own stale copy. It now writes
 * only the `masterSolution` field of each task (`persistExtractedArtifact`), so the two are disjoint and can
 * hold different locks.
 */

import { defineJob } from '../registry.js';
import { masterSolutionStep } from '../steps/master-solution.step.js';
import { taskTreeLockKey } from '../job.js';
import * as conditions from '../job-conditions.js';
import { runMasterSolutionStep } from '../../pipeline/master-solution-step.js';

export const masterSolutionExtractionJob = defineJob({
  type: 'msExtraction',

  /**
   * ONE ROW, unconditionally. `ready()` drops it when no master-solution file was uploaded, so there is nothing for
   * a caller to check first — and a run opened for an artifact that was never uploaded simply never gets a row.
   *
   * A re-upload re-runs an extraction that already completed once, so this is a genuinely NEW run; `beginProgressRun`
   * also records that the pass is a repeat, which the announcement words differently.
   */
  /**
   * Nothing but the run: the handler is the first thing that knows whether there was anything to read, so it writes the
   * status and the total itself. A re-upload re-runs an extraction that already completed once, and letting
   * `beginProgressRun` decide `reupload` is exactly right for that — it means "this step already reached a conclusion".
   */
  onStepStart: {},

  batch: {
    units: () => [{}],
  },

  /** Closes the master-solution step once no job of it is left for this exam — see `STEP_JOB_TYPES`. */
  onStepComplete: masterSolutionStep,

  /**
   * Its own key, not the shared task-tree one. Safe because the only OTHER writer it could race is the
   * structure step, and it cannot: `ready` requires a confirmed structure, and `structureConfirmationState`
   * is never set back to false anywhere in the codebase — so a confirmed exam never re-runs structure
   * extraction. If that ever changes, this has to go back to `taskTreeLockKey`.
   */
  lockKey: examId => `${taskTreeLockKey(examId)}:masterSolution`,

  ready: exam => {
    // The file was removed again — nothing will ever make this runnable.
    if (!conditions.hasMasterSolutionFile(exam)) return { verdict: 'drop', because: 'msExtraction:no-file' };
    // The bbx agent is told which tasks to look for, so it needs a tree the teacher has signed off.
    if (!conditions.structureConfirmed(exam)) {
      return { verdict: 'wait', because: 'msExtraction:structure-unconfirmed' };
    }
    // The class is the gate right after the structure one: the teacher is asked for it the moment they
    // confirm the tree, and nothing downstream runs until they answer.
    if (conditions.examClassPending(exam)) {
      return { verdict: 'wait', because: 'msExtraction:class-unresolved' };
    }
    return 'run';
  },

  maxRuntimeMs: 20 * 60_000,
  maxAttempts: 3,

  handler: async examId => {
    const result = await runMasterSolutionStep(examId);
    if (result.skipped) return { skipped: true, note: 'no master solution uploaded' };
    if (!result.ok) throw new Error('Master solution extraction reported a failure');
    return { note: 'master solution extracted' };
  },
});
